/**
 * Calendar Event Filter Drawer Component
 * Filters for searching/filtering events
 */

import { useState, useEffect } from 'react'
import {
  Box,
  Drawer,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Button,
  Stack,
  Divider,
  IconButton,
  Chip,
} from '@mui/material'
import { Close, FilterList, Clear } from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {
  CalendarEventSearchParams,
  CalendarEventStatus,
  getEventStatusLabel,
} from '@api/types/calendarEvent'

interface CalendarEventFilterDrawerProps {
  open: boolean
  onClose: () => void
  filters: CalendarEventSearchParams
  onFiltersChange: (filters: CalendarEventSearchParams) => void
  availableTags?: { id: string; name: string }[]
}

const sortOptions = [
  { value: 'startDate', label: 'Event Date' },
  { value: 'createdDate', label: 'Created Date' },
  { value: 'title', label: 'Title' },
]

const statusOptions = [
  { value: undefined, label: 'All Statuses' },
  { value: CalendarEventStatus.Published, label: getEventStatusLabel(CalendarEventStatus.Published) },
  { value: CalendarEventStatus.Draft, label: getEventStatusLabel(CalendarEventStatus.Draft) },
  { value: CalendarEventStatus.Cancelled, label: getEventStatusLabel(CalendarEventStatus.Cancelled) },
  { value: CalendarEventStatus.Completed, label: getEventStatusLabel(CalendarEventStatus.Completed) },
]

export function CalendarEventFilterDrawer({
  open,
  onClose,
  filters,
  onFiltersChange,
  availableTags = [],
}: CalendarEventFilterDrawerProps) {
  // Local state for form
  const [localFilters, setLocalFilters] = useState<CalendarEventSearchParams>(filters)
  const [startDateFrom, setStartDateFrom] = useState<Date | null>(
    filters.startDateFrom ? new Date(filters.startDateFrom) : null
  )
  const [startDateTo, setStartDateTo] = useState<Date | null>(
    filters.startDateTo ? new Date(filters.startDateTo) : null
  )

  // Sync local state when props change
  useEffect(() => {
    setLocalFilters(filters)
    setStartDateFrom(filters.startDateFrom ? new Date(filters.startDateFrom) : null)
    setStartDateTo(filters.startDateTo ? new Date(filters.startDateTo) : null)
  }, [filters])

  // Update local filter
  const updateFilter = <K extends keyof CalendarEventSearchParams>(
    key: K,
    value: CalendarEventSearchParams[K]
  ) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }))
  }

  // Toggle tag
  const toggleTag = (tagId: string) => {
    const currentTags = localFilters.tags || []
    const newTags = currentTags.includes(tagId)
      ? currentTags.filter((t) => t !== tagId)
      : [...currentTags, tagId]
    updateFilter('tags', newTags.length > 0 ? newTags : undefined)
  }

  // Apply filters
  const handleApply = () => {
    const appliedFilters: CalendarEventSearchParams = {
      ...localFilters,
      startDateFrom: startDateFrom?.toISOString(),
      startDateTo: startDateTo?.toISOString(),
    }
    onFiltersChange(appliedFilters)
    onClose()
  }

  // Clear all filters
  const handleClear = () => {
    const clearedFilters: CalendarEventSearchParams = {
      page: 1,
      pageSize: filters.pageSize,
    }
    setLocalFilters(clearedFilters)
    setStartDateFrom(null)
    setStartDateTo(null)
    onFiltersChange(clearedFilters)
  }

  // Count active filters
  const activeFilterCount = [
    localFilters.searchTerm,
    localFilters.status,
    startDateFrom,
    startDateTo,
    localFilters.tags?.length,
    localFilters.isPublic !== undefined,
  ].filter(Boolean).length

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 }, p: 3 },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FilterList />
          <Typography variant="h6">Filters</Typography>
          {activeFilterCount > 0 && (
            <Chip
              label={activeFilterCount}
              size="small"
              color="primary"
            />
          )}
        </Stack>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      {/* Filter Form */}
      <Stack spacing={3}>
        {/* Search */}
        <TextField
          label="Search"
          placeholder="Search events..."
          value={localFilters.searchTerm || ''}
          onChange={(e) => updateFilter('searchTerm', e.target.value || undefined)}
          fullWidth
          size="small"
        />

        {/* Status */}
        <FormControl fullWidth size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={localFilters.status ?? ''}
            label="Status"
            onChange={(e) =>
              updateFilter('status', e.target.value === '' ? undefined : (e.target.value as CalendarEventStatus))
            }
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value ?? 'all'} value={option.value ?? ''}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Range */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Typography variant="subtitle2" color="text.secondary">
            Date Range
          </Typography>
          <Stack direction="row" spacing={2}>
            <DatePicker
              label="From"
              value={startDateFrom}
              onChange={setStartDateFrom}
              slotProps={{
                textField: { size: 'small', fullWidth: true },
              }}
            />
            <DatePicker
              label="To"
              value={startDateTo}
              onChange={setStartDateTo}
              slotProps={{
                textField: { size: 'small', fullWidth: true },
              }}
            />
          </Stack>
        </LocalizationProvider>

        {/* Public/Private */}
        <FormControlLabel
          control={
            <Switch
              checked={localFilters.isPublic ?? true}
              onChange={(e) =>
                updateFilter('isPublic', e.target.checked ? undefined : false)
              }
            />
          }
          label="Show public events only"
        />

        <Divider />

        {/* Tags */}
        {availableTags.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {availableTags.map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  onClick={() => toggleTag(tag.id)}
                  color={localFilters.tags?.includes(tag.id) ? 'primary' : 'default'}
                  variant={localFilters.tags?.includes(tag.id) ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
            </Box>
          </Box>
        )}

        <Divider />

        {/* Sort */}
        <FormControl fullWidth size="small">
          <InputLabel>Sort By</InputLabel>
          <Select
            value={localFilters.sortBy || 'startDate'}
            label="Sort By"
            onChange={(e) =>
              updateFilter('sortBy', e.target.value as CalendarEventSearchParams['sortBy'])
            }
          >
            {sortOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>Sort Direction</InputLabel>
          <Select
            value={localFilters.sortDirection || 'asc'}
            label="Sort Direction"
            onChange={(e) =>
              updateFilter('sortDirection', e.target.value as 'asc' | 'desc')
            }
          >
            <MenuItem value="asc">Ascending</MenuItem>
            <MenuItem value="desc">Descending</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Actions */}
      <Box sx={{ mt: 'auto', pt: 3 }}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Clear />}
            onClick={handleClear}
            fullWidth
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            fullWidth
          >
            Apply Filters
          </Button>
        </Stack>
      </Box>
    </Drawer>
  )
}

export default CalendarEventFilterDrawer
